import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Chip,
  Stack,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../../../context/auth";
import { getRoutes } from "../../../../utilities/routes";
import WorkReportFilterSection, {
  WorkReportFilter,
} from "./WorkReportFilterSection";
import WorkHistoryList from "./WorkHistoryList";

interface WorkReportDialogProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  userName: string;
}

const WorkReportDialog: React.FC<WorkReportDialogProps> = ({
  open,
  onClose,
  userId,
  userName,
}) => {
  const { user } = useAuth();
  const Routes = getRoutes();
  const [showFilters, setShowFilters] = useState(true);
  const [showOnlyLatestLogs, setShowOnlyLatestLogs] = useState(false);
  const [filters, setFilters] = useState<WorkReportFilter>({
    userRef: userId,
    statusTask: undefined,
    startDate: "",
    endDate: "",
  });

  // لاگ عملکرد دیالوگ
  useEffect(() => {
    if (open) {
      console.log(`📊 WorkReport Dialog Opened:`, {
        timestamp: new Date().toISOString(),
        userId,
        userName,
        openedBy: user?.userName || "Unknown",
        userRole: user?.roleName || "Unknown",
      });
    }
  }, [open, userId, userName, user?.userName, user?.roleName]);

  // به‌روزرسانی userRef هر زمان که userId تغییر کرد
  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      userRef: userId,
    }));
  }, [userId]);

  // دریافت تاریخچه کارکرد کارمند با فیلتر
  const {
    data: workHistory,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["WorkHistory", userId, filters],
    queryFn: async () => {
      // اطمینان از اینکه userRef خالی نیست
      const requestFilters = {
        ...filters,
        userRef: userId, // همیشه userId را استفاده کن
      };

      // لاگ شروع درخواست API
      console.log(`📡 WorkReport API Request Started:`, {
        timestamp: new Date().toISOString(),
        userId,
        userName,
        requestFilters,
        requestedBy: user?.userName || "Unknown",
      });

      const response = await fetch(`${Routes.TaskLogRoutes}work-report`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${user?.token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestFilters),
      });

      const result = await response.json();

      // لاگ نتیجه API
      console.log(`📡 WorkReport API Response:`, {
        timestamp: new Date().toISOString(),
        userId,
        userName,
        status: response.status,
        dataLength: result?.data?.length || result?.length || 0,
        hasData: !!(result?.data?.length || result?.length),
      });

      return result;
    },
    enabled: open && !!user?.token && !!userId && !showFilters,
  });
  const handleApplyFilters = () => {
    // اطمینان از اینکه userRef همیشه مقدار داشته باشد
    const updatedFilters = {
      ...filters,
      userRef: userId, // همیشه userId را استفاده کن
    };

    // لاگ اعمال فیلترها
    console.log(`🔍 WorkReport Filters Applied:`, {
      timestamp: new Date().toISOString(),
      userId,
      userName,
      filters: updatedFilters,
      showOnlyLatestLogs,
      appliedBy: user?.userName || "Unknown",
    });

    setFilters(updatedFilters);
    setShowFilters(false);
  };

  const handleResetFilters = () => {
    const resetFilters = {
      userRef: userId,
      statusTask: undefined,
      startDate: "",
      endDate: "",
    };

    // لاگ ریست فیلترها
    console.log(`🔄 WorkReport Filters Reset:`, {
      timestamp: new Date().toISOString(),
      userId,
      userName,
      resetBy: user?.userName || "Unknown",
    });

    setFilters(resetFilters);
  };

  const handleBackToFilters = () => {
    // لاگ بازگشت به فیلترها
    console.log(`⬅️ WorkReport Back to Filters:`, {
      timestamp: new Date().toISOString(),
      userId,
      userName,
      backBy: user?.userName || "Unknown",
    });

    setShowFilters(true);
  };

  const handleFiltersChange = (newFilters: WorkReportFilter) => {
    setFilters(newFilters);
  };

  const handleShowOnlyLatestLogsChange = (value: boolean) => {
    setShowOnlyLatestLogs(value);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: "70vh",
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ borderBottom: "1px solid #e0e0e0", pb: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            گزارش کارکرد کارمند: {userName}
          </Typography>
          {!showFilters && (
            <Chip
              label={`تعداد کل رکوردها: ${workHistory?.data?.length || workHistory?.length || 0}`}
              color="primary"
              variant="outlined"
              sx={{ fontWeight: "bold" }}
            />
          )}
        </Stack>
      </DialogTitle>

      <DialogContent>
        {showFilters ? (
          <WorkReportFilterSection
            filters={filters}
            showOnlyLatestLogs={showOnlyLatestLogs}
            userId={userId}
            userName={userName}
            onFiltersChange={handleFiltersChange}
            onShowOnlyLatestLogsChange={handleShowOnlyLatestLogsChange}
          />
        ) : (
          <WorkHistoryList
            workHistory={workHistory}
            isLoading={isLoading}
            showOnlyLatestLogs={showOnlyLatestLogs}
            userId={userId}
            userName={userName}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: "1px solid #e0e0e0", pt: 2, gap: 1 }}>
        {showFilters ? (
          <>
            <Button
              onClick={handleResetFilters}
              color="secondary"
              variant="outlined"
              sx={{ minWidth: 120 }}
            >
              پاک کردن فیلترها
            </Button>
            <Button
              onClick={handleApplyFilters}
              variant="contained"
              color="primary"
              sx={{ minWidth: 120 }}
            >
              اعمال فیلترها
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={handleBackToFilters}
              color="secondary"
              variant="outlined"
              sx={{ minWidth: 120 }}
            >
              تغییر فیلترها
            </Button>
            <Button
              onClick={() => {
                // لاگ بستن دیالوگ
                console.log(`❌ WorkReport Dialog Closed:`, {
                  timestamp: new Date().toISOString(),
                  userId,
                  userName,
                  closedBy: user?.userName || "Unknown",
                  sessionDuration: "N/A", // می‌توانید مدت زمان باز بودن را محاسبه کنید
                });
                onClose();
              }}
              color="primary"
              variant="contained"
              sx={{ minWidth: 120 }}
            >
              بستن
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default WorkReportDialog;
